import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { API } from "@/constants";
import SmartImage from "@/components/SmartImage";

export default function Categories() {
  const [tiles, setTiles] = useState([]);

  useEffect(() => {
    Promise.all([axios.get(`${API}/categories`), axios.get(`${API}/products`)])
      .then(([cr, pr]) => {
        const cats = cr.data.categories || [];
        const products = pr.data.products || [];
        const built = cats
          .map((c) => {
            const p = products.find((x) => x.category === c.name && x.image);
            const image = c.image || (p && p.image) || "";
            return image ? { name: c.name, image } : null;
          })
          .filter(Boolean);
        if (built.length) setTiles(built);
      })
      .catch(() => {});
  }, []);

  if (!tiles.length) return null;

  return (
    <section id="categories" className="py-20 lg:py-28 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto" data-testid="categories-section">
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="font-mono text-xs uppercase tracking-[0.25em] text-kera-copper mb-6"
      >
        Our Range
      </motion.p>
      <motion.h2
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.1 }}
        className="font-display font-semibold tracking-tight text-kera-ink text-2xl sm:text-3xl lg:text-4xl mb-14"
      >
        Discover our top <span className="text-kera-copper">Categories</span>
      </motion.h2>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8" data-testid="categories-grid">
        {tiles.map((c, i) => (
          <motion.div
            key={c.name}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.6, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
          >
            <Link
              to={`/products?category=${encodeURIComponent(c.name)}`}
              data-testid={`category-card-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="group relative block aspect-[4/3] rounded-3xl overflow-hidden bg-gray-100 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
            >
              <SmartImage
                src={c.image}
                alt={c.name}
                sizes="(max-width: 1024px) 50vw, 33vw"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
              />
              <div className="absolute bottom-0 inset-x-0 bg-white px-2 py-2.5 lg:py-4">
                <p className="text-center text-[11px] lg:text-lg font-semibold text-kera-ink leading-tight">{c.name}</p>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
